/**
 * The invitation to keep this answer as a gold query.
 *
 * Offered beside the figure it is about, because that is the only moment a good
 * gold query is cheap to obtain: the reader has just asked a question and is
 * looking at an answer they trust. Asking somebody to author one later, from a
 * list of member names, gets the questions nobody actually asks.
 *
 * The question is editable and the wording is theirs. What is deliberately not
 * editable is the query or the figure: the contract re-runs the query and records
 * what it returns, so a certified number cannot come from whatever this page
 * happened to be showing.
 */
import { useState } from "react";
import { BadgeCheck, Check, Loader2 } from "lucide-react";

import type { GoldQueryOffer } from "../../services/agui";

interface GoldQueryCardProps {
  offer: GoldQueryOffer;
  accepted?: boolean;
  onAccept: (question: string) => Promise<void>;
}

export function GoldQueryCard({
  offer,
  accepted,
  onAccept,
}: GoldQueryCardProps) {
  const [question, setQuestion] = useState(offer.question);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  if (accepted) {
    return (
      <div className="gold-query-card accepted">
        <span className="gold-query-icon" aria-hidden="true">
          <Check size={17} />
        </span>
        <div className="gold-query-body">
          <strong>Kept as a gold query</strong>
          <small>
            Later versions of this context will be checked against this answer.
          </small>
        </div>
      </div>
    );
  }

  if (dismissed) return null;

  const submit = async () => {
    const wording = question.trim();
    if (!wording || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onAccept(wording);
    } catch (failure) {
      // Kept on the card rather than raised: the answer above is still valid,
      // and the user needs to know their acceptance did not take.
      setError(
        failure instanceof Error
          ? failure.message
          : "The answer could not be kept as a gold query.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gold-query-card">
      <span className="gold-query-icon" aria-hidden="true">
        <BadgeCheck size={17} />
      </span>
      <div className="gold-query-body">
        <strong>Keep this answer as a gold query?</strong>
        <small>
          It becomes a check this context is measured against, so a later version
          cannot change this number without somebody noticing.
        </small>
        <label className="gold-query-question">
          <span className="sr-only">Question to record</span>
          <input
            type="text"
            value={question}
            maxLength={500}
            disabled={busy}
            onChange={(event) => setQuestion(event.target.value)}
            aria-label="Question to record"
          />
        </label>
        {error && (
          <small className="gold-query-error" role="alert">
            {error}
          </small>
        )}
      </div>
      <div className="gold-query-actions">
        <button
          type="button"
          className="button secondary"
          onClick={submit}
          disabled={busy || !question.trim()}
        >
          {busy ? (
            <Loader2 size={15} className="spin" aria-hidden="true" />
          ) : null}
          {busy ? "Keeping…" : "Keep it"}
        </button>
        <button
          type="button"
          className="button tertiary"
          onClick={() => setDismissed(true)}
          disabled={busy}
        >
          Not this one
        </button>
      </div>
    </div>
  );
}

export default GoldQueryCard;
